import { usePaginatedFields } from '@cibo/landmanager'
import { GeometryEditorContext, LayerProvider } from '@cibo/mapgl'
import { useAuth, useWindowTitle } from '@cibo/profile'
import { Box, Breadcrumbs, Button, Grid, Link, Paper, Stack, Typography } from '@mui/material'
import {
  Feature,
  FeatureCollection,
  MultiPolygon,
  Polygon,
  featureCollection as featureCollectionCreator,
} from '@turf/turf'
import { useAgreement } from 'features/Agreements'
import {
  BulkConfirmDialog,
  ControlGroupPicker,
  ControlPlotEditor,
  useControlGroup,
  useControlGroupsForAgreement,
  useUpdateControlGroup,
} from 'features/ControlGroups'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Link as RRLink } from 'react-router-dom'
import { ROUTE } from '../../navigation/types'

export const ControlGroupsImpl = () => {
  const { t } = useTranslation('impact/pages/ControlGroups')
  useWindowTitle(t('windowTitle'))
  const { persona } = useAuth()

  const { agreementId } = useParams()
  const agreement = useAgreement(agreementId)

  const [bulkConfirmDialog, setBulkConfirmDialog] = useState<{
    open: boolean
    agreementId?: string
    groupCount?: number
  }>({
    open: false,
  })

  const [selectedControlGroup, setSelectedControlGroup] = useState<string>()
  const controlGroup = useControlGroup(selectedControlGroup)

  const updateControlGroup = useUpdateControlGroup()

  const groupFields = usePaginatedFields(0, 1000, {
    termsets: selectedControlGroup ? [selectedControlGroup] : undefined,
  })

  const fieldShapes = useMemo(() => {
    if (!groupFields.data) return

    const features: Feature<Polygon | MultiPolygon>[] = []

    groupFields.data.items.forEach(field => {
      if (field.geometryDetail) {
        features.push(field.geometryDetail.input.feature)
      }
    })

    return featureCollectionCreator(features) as FeatureCollection<Polygon | MultiPolygon>
  }, [groupFields.data])

  const onGeometryUpdated = useCallback(
    ({ features, touched }) => {
      if (!touched) return

      const controlPlot = features.features?.[0] as Feature<Polygon, MultiPolygon>
      if (!controlPlot) return

      if (controlPlot && controlGroup.data) {
        updateControlGroup.mutateAsync({
          controlGroupId: controlGroup.data.id,
          feature: controlPlot,
        })
      }
    },
    [fieldShapes, controlGroup]
  )

  const controlGroups = useControlGroupsForAgreement(agreementId)

  const allPlotsValid = controlGroups.data?.every(
    controlGroup => controlGroup.status === 'valid' || controlGroup.modelUnpaired
  )

  return (
    <Stack flex={1}>
      {persona !== 'grower' && (
        <Breadcrumbs sx={{ p: 2 }}>
          <Link component={RRLink} underline="hover" color="inherit" to={ROUTE.CONTROL_GROUPS}>
            {t('controlGroups')}
          </Link>
          <Typography color="text.primary">{agreement.data?.userModel.displayNameFull}</Typography>
        </Breadcrumbs>
      )}
      <Grid sx={{ flex: 1 }} container>
        <Grid sm={0} md={4} lg={3} position="relative" alignSelf="stretch" item>
          <Paper
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              bottom: 0,
              right: 0,
              overflow: 'auto',
              padding: 3,
              scrollbarGutter: 'stable',
            }}
          >
            <Stack spacing={2} style={{ flex: 1 }}>
              {persona === 'grower' && <Typography variant="h5">{t('controlGroups')}</Typography>}

              <Typography>{t('description')}</Typography>

              <ControlGroupPicker
                agreementId={agreementId}
                onChange={(id: string) => setSelectedControlGroup(id)}
                value={selectedControlGroup}
              />

              <Box sx={{ flex: 1 }} />
              <Button
                variant="contained"
                onClick={() => {
                  if (!agreementId || !agreement.data) return

                  setBulkConfirmDialog({
                    open: true,
                    agreementId,
                    groupCount: controlGroups.data?.length,
                  })
                }}
                data-testid="bulk-confirm-plots-button"
                disabled={!allPlotsValid}
              >
                {t('confirmPlots', { count: controlGroups.data?.length })}
              </Button>

              <BulkConfirmDialog
                {...bulkConfirmDialog}
                onClose={() => setBulkConfirmDialog({ open: false })}
              />
            </Stack>
          </Paper>
        </Grid>

        <Grid sm={12} md={8} lg={9} display="flex" alignSelf="stretch" item>
          <Box sx={{ flex: 1, display: 'flex' }}>
            <LayerProvider>
              <ControlPlotEditor
                controlGroup={controlGroup.data}
                fieldShapes={fieldShapes}
                onChange={onGeometryUpdated}
              />
            </LayerProvider>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  )
}

export const ControlGroupsDetail = () => (
  <GeometryEditorContext>
    <ControlGroupsImpl />
  </GeometryEditorContext>
)
